import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, Observable, forkJoin, of } from 'rxjs';
import { switchMap, map, tap, catchError } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { IDevice, IDeviceDetailsResponse, IDeviceMinDto, IDeviceReduced, IGetDeviceByIdForRevision } from '../../devices/models/device';
import { devicesSelector } from '../global/devices/device-selectors';
import { DeviceService } from '../../devices/services/device.service';
import { IAddServicePart, IDeleteServicePart, IServicePart, IUpdateServicePart } from 'src/app/devices/models/service-part';
import { ServicePartService } from 'src/app/devices/services/service-part.service';
import { IBaseGetByRevisionRequest, IFilter, IPagedResults, PageSize } from 'src/app/core/models/base';
import { IService, IUpdateServiceIdentifiedRequest, IUpdateServiceRequest, IUpdateServiceStatusRequest } from 'src/app/devices/models/service';
import { DeviceServiceService } from 'src/app/devices/services/service.service';
import { IUpdateWearingPartCategoryRequest } from 'src/app/devices/models/wearing-part-categories';
import { WearingPartService } from 'src/app/devices/services/wearing-part.service';
import { IUpdateWearingPartRequest } from 'src/app/devices/models/wearing-part';
import { DeviceServiceStore } from './device-service-store';
import { IAddDeviceToRevisionRequest, IDeleteDeviceToRevisionRequest } from 'src/app/revisions/models/revision';
import { RevisionService } from 'src/app/revisions/services/revision.service';
import { AttachmentService, IUploadAttachmentResponse } from 'src/app/shared/services/attachment.service';
import { IAddServiceAttachment, IServiceAttachment, IUpdateServiceAttachment } from 'src/app/devices/models/service-attachment';
import { ServiceAttachmentService } from 'src/app/devices/services/service-attachment.service';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { translate } from '@jsverse/transloco';


export interface DeviceState {
  device: IDeviceDetailsResponse | null;
  availableDevicesForInstallation: IDeviceReduced[];
  availableDevicesForRevision: IDeviceDetailsResponse[];
  devices: IDeviceDetailsResponse[];
  loading: boolean;
  pagination: IPagedResults<IDeviceDetailsResponse[]> | null;
  availableDevicesTotal: number;
}

@Injectable()
export class DeviceStore extends ComponentStore<DeviceState> {

  // Initial state
  constructor(
    private deviceServiceService: DeviceServiceService,
    private store: Store<AppStateInterface>,
    private deviceService: DeviceService,
    private attachmentService: AttachmentService,
    private appnotificationService: AppNotificationService,
    private serviceAttachmentService: ServiceAttachmentService,
    private wearingPartService: WearingPartService,
    private revisionService: RevisionService,
    private servicePartService: ServicePartService) {
      super({ 
        device: null, 
        availableDevicesForInstallation: [],
        availableDevicesForRevision: [],
        devices: [],
        loading: false,
        pagination: null,
        availableDevicesTotal: 0
      });
  }

  // Selector for the current device
  readonly selectedDevice$: Observable<IDeviceDetailsResponse | null> = this.select(state => state.device);

  // Selectors for lists of devices
  readonly availableDevicesForInstallation$: Observable<IDeviceReduced[]> = this.select(state => state.availableDevicesForInstallation);
  readonly availableDevicesForRevision$: Observable<IDeviceDetailsResponse[]> = this.select(state => state.availableDevicesForRevision);
  readonly availableDevicesTotal$ = this.select(state => state.availableDevicesTotal);

  readonly devices$: Observable<IDeviceDetailsResponse[]> = this.select(state => state.devices);
  readonly device$: Observable<IDeviceDetailsResponse> = this.select(state => state.device);
  readonly loading$ = this.select((state) => state.loading);
  readonly pagination$ = this.select(state => state.pagination);


  // Effect to load device by ID
  readonly getDeviceById = this.effect<string>(deviceId$ =>
    deviceId$.pipe(
      switchMap(deviceId =>
        this.deviceService.getDeviceById(deviceId).pipe(
          tap(device => this.patchState({ device }))
        )
      )
    )
  );

  // Effect to load devices without linked installation by company from the service and remove devices that are part of the installation
  readonly getDevicesByCompanyForInstallation = this.effect<{ companyId: string, installationId: string, keyword?: string, linkedDevices: IDeviceMinDto[] }>(params$ =>
    params$.pipe(
      switchMap(({ companyId, installationId, keyword, linkedDevices }) => {
        return this.deviceService.getDevicesWithoutInstallationByCompany({pageNumber: 0, pageSize: 25, companyId, installationId, keyword}).pipe(
          map(response => response.data), // Get the devices from the company
          map(devices => {
            // Filter out devices that are already in the installation
            const filteredDevices = devices.filter(device => 
              !(linkedDevices || []).some(installedDevice => installedDevice.id === device.id)
            );
            
            return filteredDevices;
          }),
          tap(filteredDevices => {
            // Update the local state with the filtered devices
            this.patchState({ availableDevicesForInstallation: filteredDevices });
          })
        );
      })
    )
  );

  // Effect to load available devices for revision
  readonly getAvailableDevicesForRevision = this.effect<{ 
    revisionId: string, 
    pageNumber?: number,
    pageSize?: number,
    advancedFilter?: IFilter, 
    scheduledDateFrom?: string, 
    scheduledDateTo?: string  
  }>(params$ =>
    params$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(({ revisionId, pageNumber = 0, pageSize = 25, advancedFilter, scheduledDateFrom, scheduledDateTo }) =>
        this.deviceService.getAvailableDevicesForRevision({ 
          pageNumber,
          pageSize,
          revisionId, 
          advancedFilter, 
          scheduledDateFrom, 
          scheduledDateTo 
        }).pipe(
          tap(response => {
            this.patchState({
              availableDevicesForRevision: response.data,
              availableDevicesTotal: response.totalCount
            });          
          })
        )
      )
    )
  );

  // Effect to load devices by revision
  readonly getDevicesByRevision = this.effect<IBaseGetByRevisionRequest>(params$ =>
    params$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(request =>
        this.deviceService.getDevicesByRevision(request).pipe(
          tap(response => {
            // Patch devices and pagination info into the state
            this.patchState({
              devices: response.data,
              pagination: {
                data: response.data,
                pageSize: response.pageSize,
                hasNextPage: response.hasNextPage,
                hasPreviousPage: response.hasPreviousPage,
                currentPage: response.currentPage,
                totalPages: response.totalPages,
                totalCount: response.totalCount
              },
              loading: false
            });
          })
        )
      )
    )
  );


    // Effect to load devices with wearing parts by revision
    readonly getDevicesWithWearingPartsByRevision = this.effect<IBaseGetByRevisionRequest>(params$ =>
      params$.pipe(
        tap(() => this.patchState({ loading: true })),
        switchMap(request =>
          this.deviceService.getDevicesWithWearingPartsByRevision(request).pipe(
            tap(response => {
              // Patch devices and pagination info into the state
              this.patchState({
                devices: response.data,
                pagination: {
                  data: response.data,
                  pageSize: response.pageSize,
                  hasNextPage: response.hasNextPage,
                  hasPreviousPage: response.hasPreviousPage,
                  currentPage: response.currentPage,
                  totalPages: response.totalPages,
                  totalCount: response.totalCount
                },
                loading: false
              });
            })
          )
        )
      )
    );

  // Effect to fetch and update a device by its ID
  readonly updateDeviceById = this.effect<{ request: IGetDeviceByIdForRevision }>(params$ =>
    params$.pipe(
      switchMap(({ request }) =>
        this.deviceService.getDeviceForRevision(request).pipe(
          tap(device => {
            this.patchState(state => {
              const updatedDevices = state.devices.map(d =>
                d.id === device.id ? { ...d, ...device } : d
              );
              return { devices: updatedDevices };
            });
          })
        )
      )
    )
  );

  // Effect to update service part of device
  readonly updateServicePart = this.effect<{
    request: IUpdateServicePart;
    deviceId: string;
    onSuccess?: (updated: IServicePart) => void;
  }>(params$ =>
    params$.pipe(
      switchMap(({ request, deviceId, onSuccess }) =>
        this.servicePartService.updateServicePart(request).pipe(
          tap((updatedServicePart: IServicePart) => {
            this.patchState(state => {
              const updatedDevices = state.devices.map(device => {
                if (device.id !== deviceId) return device;
  
                const updatedServices = device.services.map(service => {
                  const updatedServiceParts = (service.serviceParts ?? []).map(sp =>
                    sp.id === updatedServicePart.id ? updatedServicePart : sp
                  );
  
                  return { ...service, serviceParts: updatedServiceParts };
                });
  
                return { ...device, services: updatedServices };
              });
  
              return { devices: updatedDevices };
            });

            onSuccess?.(updatedServicePart);
          })
        )
      )
    )
  );

  // Effect to update service attachment of device
readonly updateServiceAttachment = this.effect<{
  request: IUpdateServiceAttachment;
  deviceId: string;
  onSuccess?: (updated: IServiceAttachment) => void;
}>(params$ =>
  params$.pipe(
    switchMap(({ request, deviceId, onSuccess }) =>
      this.serviceAttachmentService.updateServiceAttachment(request).pipe(
        tap((updatedServiceAttachment: IServiceAttachment) => {
          this.patchState(state => {
            const deviceIndex = state.devices.findIndex(d => d.id === deviceId);
            if (deviceIndex === -1) return state;

            const device = state.devices[deviceIndex];

            const updatedServices = device.services.map(service => {
              const updatedServiceAttachments = (service.serviceAttachments ?? []).map(sa =>
                sa.id === updatedServiceAttachment.id
                  ? { ...sa, showInReport: updatedServiceAttachment.showInReport }
                  : sa
              );

              return { ...service, serviceAttachments: updatedServiceAttachments };
            });

            const updatedDevices = [...state.devices];
            updatedDevices[deviceIndex] = { ...device, services: updatedServices };

            return { devices: updatedDevices };
          });

          onSuccess?.(updatedServiceAttachment);
        })
      )
    )
  )
);

  
  
readonly updateService = this.effect<{ request: IUpdateServiceRequest }>(params$ =>
  params$.pipe(
    switchMap(({ request }) =>
      this.deviceServiceService.updateDeviceService(request).pipe(
        tap((updatedService: IService) => {
          this.patchState(state => {
            // Find the device that contains this service
            const deviceIndex = state.devices.findIndex(d =>
              d.services.some(s => s.id === updatedService.id)
            );
            if (deviceIndex === -1) return state; // Not found, nothing to update

            const device = state.devices[deviceIndex];

            // Only replace the service that changed
            const updatedServices = device.services.map(s =>
              s.id === updatedService.id ? { ...s, ...updatedService } : s
            );

            // Replace only the device object
            const updatedDevices = [...state.devices];
            updatedDevices[deviceIndex] = { ...device, services: updatedServices };

            return { devices: updatedDevices };
          });
        })
      )
    )
  )
);

  

  // marks the device of a service as identified on site; patches only that flag so other service fields stay untouched
  readonly updateServiceIdentified = this.effect<{
    request: IUpdateServiceIdentifiedRequest;
    deviceId: string;
  }>(params$ =>
    params$.pipe(
      switchMap(({ request, deviceId }) =>
        this.deviceServiceService.updateDeviceServiceIdentified(request).pipe(
          tap(updatedService => {
            this.patchState(state => {
              const deviceIndex = state.devices.findIndex(d => d.id === deviceId);
              if (deviceIndex === -1) return state;

              const device = state.devices[deviceIndex];
              const updatedServices = device.services.map(service =>
                service.id === request.id
                  ? { ...service, identified: updatedService.identified }
                  : service
              );

              const updatedDevices = [...state.devices];
              updatedDevices[deviceIndex] = { ...device, services: updatedServices };

              return { devices: updatedDevices };
            });
          })
        )
      )
    )
  );

  readonly updateServiceStatus = this.effect<{
    request: IUpdateServiceStatusRequest;
    deviceId: string;
    onSuccess?: (updated: IService) => void;
  }>(params$ =>
    params$.pipe(
      switchMap(({ request, deviceId, onSuccess }) =>
        this.deviceServiceService.updateDeviceServiceStatus(request).pipe(
          tap(updatedServiceStatus => {
            this.patchState(state => {
              // Find the device in the state
              const deviceIndex = state.devices.findIndex(d => d.id === deviceId);
              if (deviceIndex === -1) return state;

              const device = state.devices[deviceIndex];

              // Map services: only update the one that matches serviceId
              const updatedServices = device.services.map(service =>
                service.id === request.id
                  ? {
                      ...service,
                      status: updatedServiceStatus.status,
                      maintainerFirstName: updatedServiceStatus.maintainerFirstName,
                      maintainerLastName: updatedServiceStatus.maintainerLastName,
                      dateExecuted: updatedServiceStatus.dateExecuted,
                      cancellationMessage: updatedServiceStatus.cancellationMessage
                    }
                  : service
              );

              // Patch only the affected device
              const updatedDevices = [...state.devices];
              updatedDevices[deviceIndex] = { ...device, services: updatedServices };

              return { devices: updatedDevices };
            });

            // Call callback if provided
            onSuccess?.(updatedServiceStatus);
          })
        )
      )
    )
  );


 
  // Method to remove a device from availableDevicesForInstallation by its ID
  readonly removeDeviceByIdForInstallation = this.updater((state, deviceId: string) => {
    const updatedDevices = state.availableDevicesForInstallation.filter(device => device.id !== deviceId);
    return {
      ...state,
      availableDevicesForInstallation: updatedDevices
    };
  });

  // Method to remove a device from availableDevicesForRevision by its ID
  readonly removeDeviceByIdForRevision = this.updater((state, deviceId: string) => {
    const updatedDevices = state.availableDevicesForRevision.filter(device => device.id !== deviceId);
    return {
      ...state,
      availableDevicesForRevision: updatedDevices
    };
  });

  // Method to add a device to availableDevicesForInstallation by its ID
  readonly addDeviceByIdForInstallation = this.effect<string>(deviceId$ =>
    deviceId$.pipe(
      switchMap(deviceId => 
        // Call the service to get the device by ID
        this.deviceService.getDeviceById(deviceId)
      ),
      tap((newDevice: IDeviceDetailsResponse) => {
        // Check if the device is already in the available devices
        const isDeviceAlreadyPresent = this.get().availableDevicesForInstallation.some(device => device.id === newDevice.id);

        // Add the device if it's not already present
        if (!isDeviceAlreadyPresent) {
          this.patchState({
            availableDevicesForInstallation: [...this.get().availableDevicesForInstallation, newDevice]
          });
        }
      })
    )
  );

  // Method to add a device to availableDevicesForRevision by its ID
  readonly addDeviceByIdForRevision = this.effect<string>(deviceId$ =>
    deviceId$.pipe(
      switchMap(deviceId => 
        // Call the service to get the device by ID
        this.deviceService.getDeviceById(deviceId)
      ),
      tap((newDevice: IDeviceDetailsResponse) => {
        // Check if the device is already in the available devices
        const isDeviceAlreadyPresent = this.get().availableDevicesForRevision.some(device => device.id === newDevice.id);

        // Add the device if it's not already present
        if (!isDeviceAlreadyPresent) {
          this.patchState({
            availableDevicesForRevision: [...this.get().availableDevicesForRevision, newDevice]
          });
        }
      })
    )
  );

  // add linked wearing part to device service
  readonly addServicePartEffect = this.effect<{ request: IAddServicePart, deviceId: string }>(params$ =>
    params$.pipe(
      switchMap(({ request, deviceId }) =>
        this.servicePartService.addServicePart(request).pipe(
          tap(updatedServicePart => {
            this.patchState(state => {
              const updatedDevices = state.devices.map(device => {
                if (device.id !== deviceId) return device;
  
                const updatedServices = device.services.map((service, index) => {
                  if (index !== 0) return service;
  
                  const updatedServiceParts = [...(service.serviceParts ?? []), updatedServicePart];
                  return { ...service, serviceParts: updatedServiceParts };
                });
  
                return { ...device, services: updatedServices };
              });
  
              return { devices: updatedDevices };
            });
          })
        )
      )
    )
  );

  updateWearingPart(
    request: IUpdateWearingPartRequest,
    deviceId: string,
    callback?: () => void
  ): void {
    this.wearingPartService.updateWearingPart(request).subscribe(updatedWearingPart => {
      this.patchState(state => {
        const updatedDevices = state.devices.map(device => {
          if (device.id !== deviceId) return device;
  
          const updatedWearingParts = (device.wearingParts ?? []).map(part =>
            part.id === updatedWearingPart.id ? { ...part, ...updatedWearingPart } : part
          );
  
          return { ...device, wearingParts: updatedWearingParts };
        });
  
        return { devices: updatedDevices };
      });
  
      if (callback) {
        callback();
      }
    });
  }
  

  // remove linked wearing part from device service
  readonly deleteServicePartEffect = this.effect<{ request: IDeleteServicePart, deviceId: string }>(params$ =>
    params$.pipe(
      switchMap(({ request, deviceId }) =>
        this.servicePartService.deleteServicePart(request).pipe(
          tap(() => {
            this.patchState(state => {
              const updatedDevices = state.devices.map(device => {
                if (device.id !== deviceId) return device;
  
                const updatedServices = device.services.map((service, index) => {
                  if (index !== 0) return service;
  
                  const filteredParts = (service.serviceParts || []).filter(sp => sp.id !== request.id);
                  return { ...service, serviceParts: filteredParts };
                });
  
                return { ...device, services: updatedServices };
              });
  
              return { devices: updatedDevices } as Partial<DeviceState>;
            });
          })
        )
      )
    )
  );

// Effect to remove a device from a revision
readonly removeDeviceFromRevision = this.effect<{
  request: IDeleteDeviceToRevisionRequest,
  revisionId: string,
  currentPage: number,
  pageSize: number,
  advancedFilter?: IFilter,
  serviceStatus?: number
}>(params$ =>
  params$.pipe(
    tap(() => this.patchState({ loading: true })),

    switchMap(({ request, revisionId, currentPage, pageSize, advancedFilter, serviceStatus }) =>
      this.revisionService.deleteDeviceFromRevision(request).pipe(

        // Reload the assigned devices with filters
        switchMap(() =>
          this.deviceService.getDevicesByRevision({
            pageNumber: currentPage,
            pageSize: pageSize,
            revisionId: revisionId,
            advancedFilter: advancedFilter,
            serviceStatus: serviceStatus
          })
        ),

        // Handle pagination (go back a page if empty)
        tap(response => {
          const shouldGoToPrev = response.data.length === 0 && currentPage > 1;

          if (shouldGoToPrev) {
            // Fetch previous page with filters
            this.deviceService.getDevicesByRevision({
              pageNumber: currentPage - 1,
              pageSize: pageSize,
              revisionId: revisionId,
              advancedFilter: advancedFilter,
              serviceStatus: serviceStatus
            }).subscribe(prevPageResponse => {
              this.patchState({
                devices: prevPageResponse.data,
                pagination: prevPageResponse,
                loading: false
              });
            });
          } else {
            // Normal: update devices for current page
            this.patchState({
              devices: response.data,
              pagination: response,
              loading: false
            });
          }
        })
      )
    )
  )
);

readonly addDeviceToRevision = this.effect<{
  request: IAddDeviceToRevisionRequest,
  revisionId: string,
  currentPage: number,
  pageSize: number,
  advancedFilter?: IFilter,
  scheduledDateFrom?: string,
  scheduledDateTo?: string
}>(params$ =>
  params$.pipe(
    switchMap(({ request, revisionId, currentPage, pageSize, advancedFilter, scheduledDateFrom, scheduledDateTo }) =>
      this.revisionService.addDeviceToRevision(request).pipe(
        tap(() => {
          // Get current state
          const currentDevices = this.get().availableDevicesForRevision;
          const currentTotal = this.get().availableDevicesTotal;
          
          // Remove the device from the list
          const updatedDevices = currentDevices.filter(d => d.id !== request.deviceId);
          const newTotal = currentTotal - 1;
          
          // Update state immediately for responsive UI
          this.patchState({
            availableDevicesForRevision: updatedDevices,
            availableDevicesTotal: newTotal
          });
          
          // If the current page is now empty and we're not on page 1, 
          // we need to fetch the previous page
          if (updatedDevices.length === 0 && currentPage > 1) {
            // Fetch previous page
            this.getAvailableDevicesForRevision({
              revisionId,
              pageNumber: currentPage - 1,
              pageSize,
              advancedFilter,
              scheduledDateFrom,
              scheduledDateTo
            });
          } 
          // If we removed an item but still have items, 
          // refetch to fill the page from the server
          else if (updatedDevices.length < pageSize && newTotal > updatedDevices.length) {
            // Refetch current page to get the next item to fill the gap
            this.getAvailableDevicesForRevision({
              revisionId,
              pageNumber: currentPage,
              pageSize,
              advancedFilter,
              scheduledDateFrom,
              scheduledDateTo
            });
          }
        }),
        catchError(error => {
          console.error('Error adding device to revision:', error);
          // Optionally: show error notification
          return EMPTY;
        })
      )
    )
  )
);




readonly addAttachments = this.effect<{
  attachments: FormData[],
  onSuccess?: (attachments: IServiceAttachment[]) => void
}>(params$ =>
  params$.pipe(
    tap(() => this.patchState({ loading: true })),
    switchMap(({ attachments, onSuccess }) =>
      forkJoin(attachments.map(f => this.attachmentService.upload(f))).pipe(
        switchMap(uploadedAttachments => 
          forkJoin(
            uploadedAttachments.map(uploaded => {
              const request: IAddServiceAttachment = {
                serviceId: uploaded.serviceId,
                attachmentId: uploaded.id,
                showInReport: false
              };
              return this.serviceAttachmentService.addServiceAttachment(request);
            })
          )
        ),
        tap((createdServiceAttachments: IServiceAttachment[]) => {
          this.patchState(state => {
            const updatedDevices = [...state.devices];
        
            createdServiceAttachments.forEach(sa => {
              const deviceIndex = updatedDevices.findIndex(d =>
                d.services.some(s => s.id === sa.serviceId)
              );
              if (deviceIndex === -1) return;
        
              const device = updatedDevices[deviceIndex];
        
              const updatedServices = device.services.map(service =>
                service.id === sa.serviceId
                  ? {
                      ...service,
                      serviceAttachments: [
                        ...(service.serviceAttachments ?? []),
                        { ...sa, downloadedImage: '' }
                      ]
                    }
                  : service
              );
        
              updatedDevices[deviceIndex] = {
                ...device,
                services: updatedServices
              };
            });
        
            return { devices: updatedDevices, loading: false };
          });
          onSuccess?.(createdServiceAttachments);

          this.appnotificationService.notification$.next({
            message: translate('attachment.createSuccess'),
            style: 'success'
          });
        })
      )
    )
  )
);
  
  
readonly removeAttachment = this.effect<{
  attachmentId: string;
  serviceId: string;
  onSuccess?: (deletedAttachmentId: string) => void;
}>(params$ =>
  params$.pipe(
    tap(() => this.patchState({ loading: true })),
    switchMap(({ attachmentId, serviceId, onSuccess }) =>
      this.serviceAttachmentService.deleteServiceAttachment(attachmentId).pipe(
        tap(() => {
          // Update store
          this.patchState(state => {
            const deviceIndex = state.devices.findIndex(d =>
              d.services.some(s => s.id === serviceId)
            );
            if (deviceIndex === -1) return { ...state, loading: false };

            const device = state.devices[deviceIndex];
            const updatedServices = device.services.map(service =>
              service.id === serviceId
                ? {
                    ...service,
                    serviceAttachments: service.serviceAttachments.filter(a => a.id !== attachmentId)
                  }
                : service
            );

            const updatedDevices = [...state.devices];
            updatedDevices[deviceIndex] = { ...device, services: updatedServices };

            return { devices: updatedDevices, loading: false };
          });

          // Pass deleted ID to callback
          onSuccess?.(attachmentId);

          this.appnotificationService.notification$.next({
            message: translate('attachment.deleteSuccess'),
            style: 'success'
          });
        })
      )
    )
  )
);






  readonly logStateChanges = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => {
        this.state$.subscribe(state => console.log('ComponentStore State:', state));
      })
    )
  );
}
